// CareLogix™ ORACLE — OracleConfidenceBand (Phase 33 §2)
// Animated gauge + uncertainty corridor for the active symbol.
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { predictiveApi } from "@/api/phase33Client";

function Arc({ value, label, color, testid }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const R = 56, C = 2 * Math.PI * R;
  const offset = C - (pct / 100) * C;
  return (
    <div style={S.arcWrap} data-testid={testid}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle
          cx="70" cy="70" r={R}
          fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          transform="rotate(-90 70 70)"
        />
        <text x="70" y="66" textAnchor="middle" fontSize="26" fontWeight="800" fill={PALETTE.goldBright} fontFamily="'Space Mono', monospace">{pct}</text>
        <text x="70" y="86" textAnchor="middle" fontSize="10" fill={PALETTE.slate4} fontFamily="'Space Mono', monospace">/100</text>
      </svg>
      <span style={S.arcLbl}>{label}</span>
    </div>
  );
}

export default function OracleConfidenceBand({ symbol = "AAPL", domain = "stocks", testid = "oracle-confidence-band" }) {
  const [signal, setSignal] = useState(null);
  useEffect(() => {
    let cancelled = false;
    predictiveApi.signal(symbol, domain).then((r) => { if (!cancelled) setSignal(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [symbol, domain]);
  const conf = signal?.scores?.confidence_score ?? 0;
  const opp  = signal?.scores?.opportunity_score ?? 0;
  const risk = signal?.scores?.risk_score ?? 0;
  const unc  = signal?.uncertainty_pct ?? Math.max(10, 100 - conf);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE CONFIDENCE BAND™</div>
          <h3 style={S.title}>{symbol} · Conviction Gauges</h3>
        </div>
        <span style={{ ...S.uncertaintyPill, color: PALETTE.amber, borderColor: PALETTE.amber }} data-testid={`${testid}-uncertainty`}>UNCERTAINTY {unc}%</span>
      </header>
      <div style={S.arcs}>
        <Arc value={conf} label="Confidence" color={scoreColor(conf)} testid={`${testid}-conf`} />
        <Arc value={opp}  label="Opportunity" color={scoreColor(opp)}  testid={`${testid}-opp`} />
        <Arc value={risk} label="Risk" color={scoreColor(100 - risk)} testid={`${testid}-risk`} />
      </div>
      <div style={S.note}>
        Confidence reflects ensemble agreement of the ORACLE PREDICTIVE AI ENGINE™. Uncertainty grows when sources disagree.
      </div>
      <footer style={S.foot}>Research only · Compliance Lock™ · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 16 },
  uncertaintyPill:{ fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px solid" },
  arcs:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, alignItems: "center", justifyItems: "center" },
  arcWrap:  { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  arcLbl:   { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.08em" },
  note:     { color: PALETTE.slate4, fontSize: 12, lineHeight: 1.5 },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
